import {
  Action, ConfirmModal, Portal, useModal, useNotifications,
} from '@doveit/bricks';
import React from 'react';
import { deleteInvoice } from '../../../providers/api/invoice/invoiceProvider';
import { Invoice } from '../../../providers/api/dtos';
import useRBAC from '../../../hooks/use-rbac/useRBAC';

export const DELETE_INVOICE_SUCCESS_MESSAGE = 'Il pagamento è stata eliminato correttamente.';
export const DELETE_INVOICE_ERROR_MESSAGE = 'Non è stato possibile eliminare il pagamento.';

export interface ChildFnProps {
  isLoading?: boolean,
  onOpenModal: VoidFunction,
}

export interface DeleteInvoiceActionProps {
  invoiceId: NonNullable<Invoice['id']>,
  onSuccess?: VoidFunction
  children?: (props: ChildFnProps) => React.ReactNode;
}

const DeleteInvoiceAction: React.FC<DeleteInvoiceActionProps> = ({
  invoiceId,
  onSuccess,
  children,
}) => {
  const { userIsAdmin, userIsFinance } = useRBAC();
  const [isSaving, setIsSaving] = React.useState(false);
  const deleteModal = useModal();
  const { addSuccess, addError } = useNotifications();

  const onOpenModal = React.useCallback(() => deleteModal.open(), [deleteModal]);

  const onConfirmDelete = React.useCallback(async () => {
    try {
      setIsSaving(true);

      await deleteInvoice(invoiceId);

      addSuccess(DELETE_INVOICE_SUCCESS_MESSAGE);

      setIsSaving(false);
      deleteModal.close();
      onSuccess?.();
    } catch (_) {
      addError(DELETE_INVOICE_ERROR_MESSAGE);
      setIsSaving(false);
    }
  }, [addError, addSuccess, deleteModal, invoiceId, onSuccess]);

  const onAbortDelete = React.useCallback(() => deleteModal.close(), [deleteModal]);

  if (!(userIsAdmin || userIsFinance)) return null;

  return (
    <>
      {
        children
          ? children({ onOpenModal, isLoading: isSaving })
          : (
            <Action
              label="Elimima fattura"
              onClick={onOpenModal}
              loading={isSaving}
              aria-label="Azione per eliminare il pagamento"
            />
          )
      }
      <Portal>
        <ConfirmModal
          {...deleteModal}
          onConfirm={onConfirmDelete}
          onAbort={onAbortDelete}
          title="Conferma eliminazione pagamento"
          loading={isSaving}
          variant="critical"
          aria-label="Modale di conferma per eliminare il pagamento"
        >
          Confermando l&apos;operazione, il pagamento sarà eliminato.
        </ConfirmModal>
      </Portal>
    </>
  );
};

export default DeleteInvoiceAction;
